import { createClient, navigatorLock, processLock } from '@supabase/supabase-js';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, Platform } from 'react-native';

import { captureException, captureMessage } from '../services/telemetry';

// Environment variables (prefix EXPO_PUBLIC_ required for Expo)
const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
        '[Supabase] Variáveis de ambiente ausentes. Defina EXPO_PUBLIC_SUPABASE_URL e EXPO_PUBLIC_SUPABASE_ANON_KEY no arquivo .env e reinicie o bundler com: expo start -c'
    );
}

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;

// React Native fetch NÃO tem timeout padrão. Em redes móveis (IPv6/Happy-Eyeballs,
// NAT de operadora, MTU), uma conexão pode estabelecer mas nunca responder,
// travando o request para sempre → queries ficam em 'pending' infinito → telas
// em loading eterno. Em WiFi de dev isso raramente dispara, daí o bug só aparecer
// no APK. Timeout converte o hang em rejeição, que o React Query trata (retry/erro).
const FETCH_TIMEOUT_MS = 10000;
const SLOW_WEB_REQUEST_MS = 1500;
const TELEMETRY_THROTTLE_MS = 60 * 1000;
const lastTelemetryAtByRequest = new Map();

const getRequestMetadata = (input, init) => {
    const rawUrl = typeof input === 'string' ? input : input?.url;
    let path = 'unknown';

    try {
        path = new URL(rawUrl).pathname;
    } catch {
        // Nunca envie query string, token ou payload para a telemetria.
        path = typeof rawUrl === 'string' ? rawUrl.split('?')[0] : 'unknown';
    }

    return {
        method: init?.method || (typeof input === 'object' ? input?.method : null) || 'GET',
        path,
    };
};

const shouldReportRequest = (kind, metadata) => {
    const key = `${kind}:${metadata.method}:${metadata.path}`;
    const now = Date.now();
    const lastReportedAt = lastTelemetryAtByRequest.get(key) || 0;
    if (now - lastReportedAt < TELEMETRY_THROTTLE_MS) return false;
    lastTelemetryAtByRequest.set(key, now);
    return true;
};

/**
 * Alguns navegadores Android expõem `navigator.locks`, mas rejeitam `request()`
 * com DOMException/NetworkError antes de executar o callback. O auth-js guarda
 * essa primeira Promise rejeitada em `initializePromise`; a partir daí TODA
 * chamada de sessão falha e nenhuma tela autenticada consegue buscar dados.
 *
 * Mantemos o lock multiaba oficial quando ele funciona. O fallback process-local
 * só é usado quando a API do navegador falha ANTES de iniciar a operação. Erros
 * da própria operação e timeouts legítimos do lock nunca são repetidos.
 */
const webAuthLock = async (name, acquireTimeout, fn) => {
    if (!globalThis?.navigator?.locks?.request) {
        return processLock(name, acquireTimeout, fn);
    }

    let operationStarted = false;
    try {
        return await navigatorLock(name, acquireTimeout, async () => {
            operationStarted = true;
            return fn();
        });
    } catch (error) {
        if (operationStarted || error?.isAcquireTimeout) throw error;
        return processLock(name, acquireTimeout, fn);
    }
};

// Storage (download/upload de mídia) não deve ter o mesmo teto que REST/Auth:
// arquivos grandes em rede lenta legitimamente passam de 10s. REST de dados, não.
const isStorageRequest = (url) =>
    typeof url === 'string' && url.includes('/storage/v1/object');

const fetchWithTimeout = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (isStorageRequest(url)) {
        return fetch(input, init); // mídia: sem teto de timeout
    }

    const startedAt = Date.now();
    const requestMetadata = getRequestMetadata(input, init);
    const controller = new AbortController();

    // O timeout NÃO pode depender de controller.abort() rejeitar o fetch: no
    // runtime do APK (Hermes/RN), com socket meio-aberto (NAT de operadora /
    // IPv6 — ver comentário acima), o abort() não settla a promise do fetch →
    // ela fica pendente para sempre → queryFn em 'pending' eterno → spinner
    // infinito nas telas. Por isso usamos Promise.race contra uma promise que
    // REJEITA por conta própria após o teto. Ainda chamamos abort() para liberar
    // o socket, mas o resultado não depende mais disso.
    let timeoutId;
    const timeoutPromise = new Promise((_resolve, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort();
            const timeoutError = new Error(`Supabase request timeout (${FETCH_TIMEOUT_MS}ms)`);
            timeoutError.name = 'TimeoutError';
            reject(timeoutError);
        }, FETCH_TIMEOUT_MS);
    });

    // Compõe com signal externo (ex.: o próprio Supabase pode passar um).
    // Listener nomeado p/ permitir remoção no finally (evita leak se o signal
    // externo for de vida longa).
    const onExternalAbort = () => controller.abort();
    if (init.signal) {
        if (init.signal.aborted) {
            controller.abort();
        } else {
            init.signal.addEventListener('abort', onExternalAbort, { once: true });
        }
    }

    return Promise.race([
        fetch(input, { ...init, signal: controller.signal }),
        timeoutPromise,
    ])
        .then((response) => {
            const durationMs = Date.now() - startedAt;
            if (
                Platform.OS === 'web'
                && durationMs >= SLOW_WEB_REQUEST_MS
                && shouldReportRequest('slow', requestMetadata)
            ) {
                captureMessage('Slow Supabase request', {
                    scope: 'supabase-fetch',
                    ...requestMetadata,
                    durationMs,
                    status: response?.status,
                });
            }
            return response;
        })
        .catch((error) => {
            if (Platform.OS === 'web' && shouldReportRequest('error', requestMetadata)) {
                captureException(error, {
                    scope: 'supabase-fetch',
                    ...requestMetadata,
                    durationMs: Date.now() - startedAt,
                });
            }
            throw error;
        })
        .finally(() => {
            clearTimeout(timeoutId);
            init.signal?.removeEventListener?.('abort', onExternalAbort);
        });
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        storage: AsyncStorage,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
        // Web Locks quebrado em alguns navegadores Android não pode inutilizar
        // permanentemente o bootstrap da sessão. Nativo preserva o lock padrão.
        lock: Platform.OS === 'web' ? webAuthLock : undefined,
    },
    global: {
        fetch: fetchWithTimeout,
    },
});

// Refresh proativo de token amarrado ao ciclo de vida do app (padrão oficial
// RN/Supabase). POR QUÊ: `autoRefreshToken: true` depende de um timer interno
// que o SO SUSPENDE quando o app vai para segundo plano. Ao reabrir depois de
// horas/dias, o access token (TTL ~1h) está expirado e o timer não rodou — a
// PRIMEIRA query da tela corre atrás do refresh, empilhando retries/timeouts =
// "Perfil e Comunidade carregando em loop". Ligar/desligar o auto-refresh no
// AppState mantém o token fresco antes das telas pedirem dado.
//   active            -> startAutoRefresh (renova já e reativa o timer)
//   background/inactive -> stopAutoRefresh (não desperdiça refresh sem uso)
if (Platform.OS !== 'web') {
    AppState.addEventListener('change', (state) => {
        if (state === 'active') {
            supabase.auth.startAutoRefresh().catch(() => {});
        } else {
            supabase.auth.stopAutoRefresh().catch(() => {});
        }
    });

    // Boot: o app já sobe em 'active', mas o listener acima só dispara em
    // MUDANÇA de estado. Na web o auth-js já faz isso por visibilitychange;
    // duplicar esse controle removia o listener interno e criava timers extras.
    if (AppState.currentState === 'active') {
        supabase.auth.startAutoRefresh().catch(() => {});
    }
}

export default supabase;
